import { filterSafeAnalyticsProperties } from "@ths/shared/utils";
import posthog from "posthog-js";

import type {
  BasecampAnalyticsEvent,
  BasecampAnalyticsProperties,
  BasecampAnalyticsSurface,
  PostHogSafeProperties,
} from "./events";

interface InitializeAnalyticsOptions {
  environment?: string;
  host?: string;
  key?: string;
  replayEnabled?: boolean;
}

interface RouteViewInput {
  pathname: string;
  search?: string;
  surface: BasecampAnalyticsSurface;
}

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const DEFAULT_ANALYTICS_ENVIRONMENT = "unknown";
const BASECAMP_ANALYTICS_SCHEMA_VERSION = 1;
const TRUE_VALUES = new Set(["1", "true", "yes"]);

let initialized = false;
let analyticsEnvironment = DEFAULT_ANALYTICS_ENVIRONMENT;

const isEnabled = () => initialized;

const normalizeEnvironment = (environment?: string): string => {
  const trimmedEnvironment = environment?.trim();
  return trimmedEnvironment || DEFAULT_ANALYTICS_ENVIRONMENT;
};

const compactProperties = (
  properties: BasecampAnalyticsProperties,
): PostHogSafeProperties => {
  const mapped: Record<keyof BasecampAnalyticsProperties, string> = {
    basecampId: "basecamp_id",
    emailProvided: "email_provided",
    errorCategory: "error_category",
    flow: "flow",
    hasChecklist: "has_checklist",
    hasEmailOnFile: "has_email_on_file",
    hasPrefilledContact: "has_prefilled_contact",
    hasMultipleReservations: "has_multiple_reservations",
    hasVehicle: "has_vehicle",
    isUrgent: "is_urgent",
    missingItemCount: "missing_item_count",
    outcome: "outcome",
    photoCount: "photo_count",
    printStatus: "print_status",
    reservationCount: "reservation_count",
    resetReason: "reset_reason",
    route: "route",
    step: "step",
    surface: "surface",
    verificationStatus: "verification_status",
  };

  const compacted = Object.entries(properties).reduce<PostHogSafeProperties>(
    (result, [key, value]) => {
      if (value === undefined) return result;
      const mappedKey = mapped[key as keyof BasecampAnalyticsProperties];
      if (!mappedKey) return result;
      result[mappedKey] = value;
      return result;
    },
    {
      app: "client-basecamp",
      environment: analyticsEnvironment,
      schema_version: BASECAMP_ANALYTICS_SCHEMA_VERSION,
    },
  );

  return filterSafeAnalyticsProperties(compacted);
};

export const initializeAnalytics = ({
  environment,
  host = DEFAULT_POSTHOG_HOST,
  key,
  replayEnabled = false,
}: InitializeAnalyticsOptions = {}) => {
  const trimmedKey = key?.trim();
  if (!trimmedKey || initialized) return;

  analyticsEnvironment = normalizeEnvironment(environment);
  posthog.init(trimmedKey, {
    api_host: host,
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: !replayEnabled,
    mask_all_element_attributes: true,
    mask_all_text: true,
    persistence: "localStorage+cookie",
    person_profiles: "never",
  });
  initialized = true;
};

export const initializeAnalyticsFromEnv = () => {
  initializeAnalytics({
    environment:
      import.meta.env.VITE_POSTHOG_ENVIRONMENT ?? import.meta.env.MODE,
    host: import.meta.env.VITE_POSTHOG_HOST,
    key: import.meta.env.VITE_POSTHOG_KEY,
    replayEnabled: TRUE_VALUES.has(
      (import.meta.env.VITE_POSTHOG_REPLAY_ENABLED ?? "").toLowerCase(),
    ),
  });
};

export const trackBasecampEvent = (
  event: BasecampAnalyticsEvent,
  properties: BasecampAnalyticsProperties = {},
) => {
  if (!isEnabled()) return;
  posthog.capture(event, compactProperties(properties));
};

export const trackRouteView = ({ pathname, surface }: RouteViewInput): void => {
  trackBasecampEvent("basecamp_route_viewed", {
    route: pathname,
    surface,
  });
};

export const resetAnalytics = () => {
  if (!isEnabled()) return;
  posthog.reset();
};

export const __resetAnalyticsForTests = () => {
  initialized = false;
  analyticsEnvironment = DEFAULT_ANALYTICS_ENVIRONMENT;
};
